import React, {
  ForwardedRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import {
  StewartDialog,
  StewartDialogActions,
} from "controls/global/dialogs/stewart-dialog/StewartDialog";
import { Box, IconButton, Typography, styled } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  borderRadius,
  colors,
  fontFamily,
  fontSize,
  fontWeight,
  gradients,
  iconSize,
  padding,
} from "theme/defaultStyle";
import ActionButton from "controls/global/action-button";
import { DialogRefType } from "entities/UIModel";
import EditFilledWhiteIcon from "theme/icons/EditFilledWhiteIcon";
import { TitleSearchProduct } from "entities/UIModel";
import { TextInput } from "controls/global/text-input-field";
import { axiosSecuredInstance } from "configurations/axiosConfig";
import { showToast } from "controls/global/stewart-toast/showToast";
import { AxiosError } from "axios";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ConfirmationDialogV2 from "controls/global/dialogs/confirmation-dialog/ConfirmationDialogV2";

const StyledDialog = styled(StewartDialog)({
  "& > .MuiDialog-container > .MuiDialog-paper": {
    maxHeight: "calc(95vh)",
    maxWidth: "830px", // "calc(80vw)",
    minWidth: "830px", // "calc(40vw)",
    display: "flex",
    rowGap: "24px",
    justifyContent: "flex-start",
    padding: "36px 40px 40px 40px",
    overflowX: "auto",
  },
});

const StyledDialogTitle = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  columnGap: "24px",
  "& .MuiTypography-root": {
    fontFamily: fontFamily.inter,
    fontStyle: "normal",
    fontSize: fontSize.xlarge1,
    fontWeight: fontWeight.bold1,
    lineHeight: "normal",
  },
});

const IconContainer = styled("div")({
  background: gradients.blueGradient10,
  borderRadius: borderRadius.large,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "56px",
  height: "56px",
  padding: padding.large1,
  "& .MuiSvgIcon-root": {
    color: colors.white,
    fontSize: iconSize.medium2,
  },
});

const StyledDialogContent = styled(Box)({
  // marginBottom: margin.xlarge1,
  display: "flex",
  gap: "24px",
  fontSize: fontSize.medium,
  fontFamily: fontFamily.inter,
  fontWeight: fontWeight.normal1,
});

const StyledDialogAction = styled(StewartDialogActions)({
  justifyContent: "right",
  paddingBottom: padding.zero,
  paddingTop: padding.zero,
  paddingRight: padding.zero,
  paddingLeft: padding.zero,
});

const CloseIconContainer = styled("div")({
  background: colors.grey10,
  borderRadius: borderRadius.xlarge1,
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  marginLeft: "auto",
  marginBottom: "auto",
  width: "32px",
  height: "32px",
  padding: "4px",
  "& .iconButton": {
    backgroundColor: colors.grey26,
    display: "flex",
    padding: padding.xsmall1,
    alignItems: "flex-start",
  },
});

const ErrorMessageContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
});

const ErrorMessage = styled(Box)({
  color: colors.red01,
  fontSize: fontSize.medium,
  fontFamily: fontFamily.inter,
  padding: `${padding.zero} ${padding.small}`,
});

const FILE_NUMBER_UPDATE_SUCCESS = "FileID and FileNumber updated";
const OVERRIDE_AND_RESEND_SUCCESS = "FileID and File Number cleared successfully";
const OVERRIDE_AND_RESEND_CONFIRMATION_MESSAGE = "This will override any duplicate check for the current file and resend as a new order to ResWare. Are you sure?";

type Props = {
  currentProduct: TitleSearchProduct;
  setShowErrorDialog: (show: boolean) => void;
  handleRefresh?: () => void;
};

const UpdateFileNumberDialog = (
  { currentProduct, setShowErrorDialog, handleRefresh }: Props,
  ref: ForwardedRef<DialogRefType>,
) => {
  const [open, setOpen] = useState<boolean>(false);
  const [saveDisabled, setSaveDisabled] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [showOverrideAndResendConfirmation, setShowOverrideAndResendConfirmation] = useState<boolean>(false);
  const [isOverrideAndResending, setIsOverrideAndResending] = useState<boolean>(false);

  const [externalReferenceID, setExternalReferenceID] = useState<number>(
    currentProduct?.externalReferenceID || 0,
  );
  const [externalFileNumber, setExternalFileNumber] = useState<string>(
    currentProduct?.externalFileNumber || "",
  );

  useImperativeHandle(ref, () => ({
    open: () => {
      setOpen(true);
      setSaveDisabled(true);
      setExternalReferenceID(currentProduct?.externalReferenceID || 0);
      setExternalFileNumber(currentProduct?.externalFileNumber || "");
      setErrorMessage("");
    },
    close: () => {
      setOpen(false);
      setSaveDisabled(true);
    },
  }));

  function handleOnClose() {
    setOpen(false);
  }

  const handleOnChange = (event: React.ChangeEvent<any>) => {
    const { name, value } = event.target;

    if (name === "externalReferenceID") {
      setExternalReferenceID(value);
    } else if (name === "externalFileNumber") {
      setExternalFileNumber(value);
    }
  };

  async function handleOnSave() {
    setIsUpdating(true);
    try {
      await axiosSecuredInstance.post("/order/UpdateOrderRWFileNumber", {
        productID: currentProduct?.productID,
        externalFileNumber,
        externalReferenceID,
        orderID: currentProduct?.orderID,
        fileID: currentProduct?.fileID,
        identityKey: currentProduct?.identityKey,
      });
      handleRefresh && handleRefresh();

      showToast({
        type: "success",
        title: FILE_NUMBER_UPDATE_SUCCESS,
      });
      setOpen(false);
    } catch (error) {
      if (error instanceof AxiosError && error.response?.data?.errorMessage) {
        setErrorMessage(error.response?.data?.errorMessage);
      } else {
        handleOnClose();
        setShowErrorDialog(true);
      }
    } finally {
      setIsUpdating(false);
    }
  }

  async function handleOverrideAndResend() {
    setIsOverrideAndResending(true);
    try {
      await axiosSecuredInstance.post("/order/UpdateOrderRWFileNumber", {
        productID: currentProduct?.productID,
        orderID: currentProduct?.orderID,
        fileID: currentProduct?.fileID,
        identityKey: currentProduct?.identityKey,
        isOverrideResend: true,
      });
      handleRefresh && handleRefresh();

      showToast({
        type: "success",
        title: OVERRIDE_AND_RESEND_SUCCESS,
      });
      setShowOverrideAndResendConfirmation(false);
      setOpen(false);
    } catch (error) {
      setShowOverrideAndResendConfirmation(false);
      if (error instanceof AxiosError && error.response?.data?.errorMessage) {
        setErrorMessage(error.response?.data?.errorMessage);
      } else {
        handleOnClose();
        setShowErrorDialog(true);
      }
    } finally {
      setIsOverrideAndResending(false);
    }
  }

  useEffect(() => {
    if (
      externalReferenceID &&
      externalFileNumber &&
      (currentProduct?.externalReferenceID !== externalReferenceID ||
        currentProduct?.externalFileNumber !== externalFileNumber)
    ) {
      setSaveDisabled(false);
    } else {
      setSaveDisabled(true);
    }
  }, [externalReferenceID, externalFileNumber, currentProduct]);

  return (
    <>
      <StyledDialog open={open}>
        <StyledDialogTitle>
          <IconContainer>
            <EditFilledWhiteIcon></EditFilledWhiteIcon>
          </IconContainer>
          <Typography>Edit FileID and File Number</Typography>
          <CloseIconContainer>
            <IconButton className="iconButton" onClick={handleOnClose}>
              <CloseIcon />
            </IconButton>
          </CloseIconContainer>
        </StyledDialogTitle>
        <StyledDialogContent>
          <TextInput
            name="externalReferenceID"
            placeholder="FileID"
            label="FileID"
            value={externalReferenceID}
            onChange={handleOnChange}
            hasError={!!errorMessage}
          />
          <TextInput
            name="externalFileNumber"
            placeholder="File Number"
            label="File Number"
            value={externalFileNumber}
            onChange={handleOnChange}
            hasError={!!errorMessage}
          />
        </StyledDialogContent>
        {errorMessage && (
          <ErrorMessageContainer>
            <ErrorOutlineIcon sx={{ color: colors.red01 }} fontSize="large" />
            <ErrorMessage>{errorMessage}</ErrorMessage>
          </ErrorMessageContainer>
        )}

        <StyledDialogAction className="DialogAction">
          <ActionButton
            variant="outlined"
            color="primary"
            onClick={handleOnClose}
          >
            Cancel
          </ActionButton>
          {!!currentProduct?.externalReferenceID && !!currentProduct?.externalFileNumber && (
            <ActionButton
              variant="outlined"
              color="secondary"
              onClick={() => setShowOverrideAndResendConfirmation(true)}
              disabled={isUpdating || isOverrideAndResending}
            >
              Override And Resend
            </ActionButton>
          )}
          <ActionButton
            variant="outlined"
            color="primary"
            onClick={handleOnSave}
            disabled={saveDisabled || isUpdating}
          >
            Update
          </ActionButton>
        </StyledDialogAction>
      </StyledDialog>
      <ConfirmationDialogV2
        isOpen={showOverrideAndResendConfirmation}
        mainMessage="Override and Resend"
        additionalMessage={OVERRIDE_AND_RESEND_CONFIRMATION_MESSAGE}
        onYes={handleOverrideAndResend}
        disableYes={isOverrideAndResending}
        onNo={() => setShowOverrideAndResendConfirmation(false)}
        onCancel={() => setShowOverrideAndResendConfirmation(false)}
        buttonNoText="Cancel"
      />
    </>
  );
};

export default forwardRef<DialogRefType, Props>(UpdateFileNumberDialog);
